/**
 * My account (not one workspace): email, sign-in methods and in-app account deletion (sessions moved to Settings →
 * Security: SecuritySettingsViewModel) (App Store 5.1.1(v), Google Play). Deleting needs a typed confirmation (my email) and is refused while I'm
 * the primary or last owner of a workspace (`deletionBlockedBy`). When it succeeds `deleted` turns true and the app
 * signs out locally (the server already revoked every session). Online only.
 */
import type { AccountResponse, OwnedWorkspace } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { errorKind, type ApiFailure } from "../ports.ts";
import type { SettingsDeps } from "./SettingsSupport.ts";
import { ViewModel } from "./ViewModel.ts";

export type AccountSettingsState = {
  status: "loading" | "ready" | "error";
  account?: AccountResponse;
  /** Workspaces whose ownership must be transferred before deleting. */
  blockers: OwnedWorkspace[];
  /** The confirmation dialog. */
  deleting: boolean;
  typedEmail: string;
  canConfirmDelete: boolean;
  deleteBusy: boolean;
  deleteError?: MessageDescriptor;
  /** The account is gone: sign out. */
  deleted: boolean;
  notice?: MessageDescriptor;
};

export class AccountSettingsViewModel extends ViewModel<AccountSettingsState> {
  private readonly deps: SettingsDeps;

  constructor(deps: SettingsDeps) {
    super({
      status: "loading",
      blockers: [],
      deleting: false,
      typedEmail: "",
      canConfirmDelete: false,
      deleteBusy: false,
      deleted: false,
    });
    this.deps = deps;
  }

  async load(): Promise<void> {
    await this.loadAccount();
  }

  private async loadAccount(): Promise<void> {
    try {
      const account = await this.deps.api.account();
      if (this.isDisposed) return;
      const blocked = new Set(account.deletionBlockedBy);
      this.update({ status: "ready", account, blockers: account.ownedWorkspaces.filter((w) => blocked.has(w.id)) });
    } catch {
      if (!this.isDisposed && this.state.account === undefined) this.update({ status: "error" });
    }
  }

  private matches(typed: string): boolean {
    const email = this.state.account?.email;
    return email !== undefined && typed.trim().toLowerCase() === email.toLowerCase();
  }

  /** Opens the confirmation (only when nothing blocks it). */
  startDelete(): void {
    if (this.state.blockers.length > 0 || this.state.account === undefined) return;
    this.update({ deleting: true, typedEmail: "", canConfirmDelete: false, deleteError: undefined });
  }

  setTypedEmail(v: string): void {
    this.update({ typedEmail: v, canConfirmDelete: this.matches(v), deleteError: undefined });
  }

  cancelDelete(): void {
    if (this.state.deleteBusy) return;
    this.update({ deleting: false, typedEmail: "", canConfirmDelete: false, deleteError: undefined });
  }

  async confirmDelete(): Promise<void> {
    const s = this.state;
    if (!s.canConfirmDelete || s.deleteBusy) {
      if (!this.matches(s.typedEmail)) this.update({ deleteError: msg("account.delete.mismatch") });
      return;
    }
    this.update({ deleteBusy: true, deleteError: undefined });
    try {
      await this.deps.api.deleteAccount({ confirmEmail: s.typedEmail.trim() });
      if (!this.isDisposed) this.update({ deleteBusy: false, deleting: false, deleted: true });
    } catch (e) {
      if (this.isDisposed) return;
      const code = (e as ApiFailure).code;
      const deleteError =
        code === "ownership_transfer_required"
          ? msg("account.delete.ownership")
          : errorKind(e) === "network"
            ? msg("settings.offline")
            : code === "confirmation_mismatch"
              ? msg("account.delete.mismatch")
              : msg("account.delete.failed");
      this.update({ deleteBusy: false, deleteError });
      if (code === "ownership_transfer_required") void this.loadAccount();
    }
  }

  dismissNotice(): void {
    this.update({ notice: undefined });
  }
}
